import { Button, Stack, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { TextInputFieldProps } from 'utoniq-ui'

import {
  GPSMapModal,
  GPSMapModalProps,
} from '@/components/pages/ConsoleMissionCreate/template/GPSMapModal'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

import { CreateEventValue } from '../EventCreateForm'
import { TextInputCell } from './Commons'

type EventInputLocationCellProps = {
  artistId: string
  location: CreateEventValue['location']
  inputApproximateAddress: Omit<TextInputFieldProps, 't' | 'type'>
  inputMapUri: Omit<TextInputFieldProps, 't' | 'type'>
  setValue: UseFormSetValue<CreateEventValue>
  gpsClientInfo: {
    permission: boolean
    currentLocation?: GPSMapModalProps['currentData']
  }
  onPlaceSearch: GPSMapModalProps['onSearch']
  predictions: GPSMapModalProps['predictions']
  place: GPSMapModalProps['place']
  onSelectPlace: GPSMapModalProps['onSelectPlace']
  onRequestGPSPermission: () => void
}

export const EventInputLocationCell: Cell<EventInputLocationCellProps> = ({
  artistId,
  location,
  inputApproximateAddress,
  inputMapUri,
  setValue,
  gpsClientInfo,
  place,
  predictions,
  onRequestGPSPermission,
  onSelectPlace,
  onPlaceSearch,
}) => {
  const { t } = useTranslation('consoleEventCreate')
  const [openGPSMapModal, setOpenGPSMapModal] = useState<boolean>(false)

  const currentPlace = useMemo<GPSMapModalProps['place']>(() => {
    if (place) {
      return place
    }
    if (location?.latitude && location?.longitude) {
      return {
        formattedAddress: location.approximateAddress ?? '',
        location: {
          lat: location.latitude,
          lng: location.longitude,
        },
      }
    }
    return null
  }, [
    location?.approximateAddress,
    location?.latitude,
    location?.longitude,
    place,
  ])

  return (
    <CellFrame sx={{ pb: 3 }}>
      <Typography variant="h5" pb={2}>
        {t('location', '会場')}
      </Typography>
      <Stack>
        <Typography variant="subtitle1" pb={1}>
          {t('approximateAddressLabel', '地図の設定')}
        </Typography>
        <TextInputCell
          type="text"
          {...inputApproximateAddress}
          titleProps={{ variant: 'body2', color: 'text.secondary' }}
          textFieldProps={{
            helperText: inputApproximateAddress?.error?.message,
            InputProps: {
              readOnly: !(location?.longitude && location?.latitude),
              inputProps: { sx: { px: 4, py: 3 } },
            },
          }}
        />
        <Stack alignItems="center">
          <Button
            size="small"
            onClick={() => {
              onRequestGPSPermission()
              setOpenGPSMapModal(true)
            }}
          >
            {t('set', '設定する')}
          </Button>
        </Stack>
      </Stack>
      <Stack pt={4}>
        <Typography variant="subtitle1" pb={1}>
          {t('mapUriLabel', '会場MAP')}
        </Typography>
        <TextInputCell
          type="text"
          {...inputMapUri}
          titleProps={{ variant: 'body2', color: 'text.secondary' }}
        />
      </Stack>
      <GPSMapModal
        artistId={artistId}
        currentData={gpsClientInfo?.currentLocation}
        onSearch={onPlaceSearch}
        predictions={predictions}
        onSelectPlace={onSelectPlace}
        place={currentPlace}
        open={openGPSMapModal}
        onSpecify={({ latitude, longitude, formattedAddress }) => {
          setValue('location.approximateAddress', formattedAddress as never, {
            shouldValidate: true,
            shouldDirty: true,
          })
          setValue('location.latitude', latitude as never, {
            shouldValidate: true,
            shouldDirty: true,
          })
          setValue('location.longitude', longitude as never, {
            shouldValidate: true,
            shouldDirty: true,
          })
          setOpenGPSMapModal(false)
        }}
        onClose={() => setOpenGPSMapModal(false)}
      />
    </CellFrame>
  )
}
